import { dist, midpoint } from "vector";

// Tile pixels (300dpi) per PDF point (72dpi).
const sf = 300 / 72;

const dimensionTextAbbrevs = [
  "AFF",
  "AFFL",
  "BOB",
  "BOD",
  "BOF",
  "CC",
  "OC",
  "CL",
  "DIM",
  "EL",
  "ELEV",
  "FF",
  "FFL",
  "MAX",
  "MIN",
  "NTS",
  "RO",
  "REF",
  "TYP",
  "TO",
];

function removeNonDimensionText(text) {
  const dottedSet = new Set(["BOB", "BOD", "BOF", "OC", "FF", "RO"]);

  const patterns = dimensionTextAbbrevs.flatMap((abbrev) => {
    const base = `\\b${abbrev}\\.?(?!\\w)`;
    if (!dottedSet.has(abbrev)) return [base];
    const dotted = `\\b${abbrev.split("").join("\\.")}\\.?(?!\\w)`;
    return [base, dotted];
  });

  const pattern = new RegExp(`(${patterns.join("|")})`, "gi");
  return text.replace(pattern, "").replace(/\s+/g, " ").trim();
}

/**
 * The parseable text region closest to the picked span's center.
 * `parse(text) -> inches | null` is injected so this stays testable without
 * a built dimtext.
 */
function nearestDimension(regions, ctr, parse) {
  const dims = (regions || [])
    .map((r) => {
      const text = removeNonDimensionText(r.content || "");
      const inches = parse(text);
      if (!inches) return null;
      return { text, inches, dist: dist(ctr, r.insertion_point) };
    })
    .filter(Boolean);

  const nearest = dims.sort((a, b) => a.dist - b.dist)[0];
  return nearest ? { text: nearest.text, inches: nearest.inches } : null;
}

function tileSize(w, h, sr) {
  const r = sr % 360;
  if ([90, 270].includes(r)) {
    return { x: h * sf, y: w * sf };
  }

  return { x: w * sf, y: h * sf };
}

function sheetPoint(transformTilePoint, p, sheetLimits, sheetRotation) {
  const w = sheetLimits[1][0] - sheetLimits[0][0];
  const h = sheetLimits[1][1] - sheetLimits[0][1];
  const ts = tileSize(w, h, sheetRotation);
  const pt = transformTilePoint(p, sheetRotation, sf, ts);

  return {
    x: pt.x + sheetLimits[0][0],
    y: pt.y + sheetLimits[0][1],
  };
}

// A square search window around the picked span, padded 25px each side —
// the same region the old pick-a-dimension tool searched.
function searchClip(pts) {
  const minX = Math.min(pts[0].x, pts[1].x);
  const maxX = Math.max(pts[0].x, pts[1].x);
  const minY = Math.min(pts[0].y, pts[1].y);
  const maxY = Math.max(pts[0].y, pts[1].y);

  const ht = maxY - minY;
  const size = Math.max(ht, maxX - minX) + 50;

  return {
    x: minX - 25,
    y: minY + ht / 2 - size / 2,
    width: size,
    height: size,
  };
}

/**
 * Read the PDF text layer near the picked points and return the most likely
 * dimension as a prefill suggestion: `{ text, inches }` or null. Never
 * authoritative — the user confirms or retypes the value in the dialog.
 * `rpc` is api.rpc, injected (with `parse`) to keep this module import-light.
 */
async function readDimensionSuggestion({ doc, page, sheet, pts, parse, rpc }) {
  try {
    // Lazy: the geometry helper's import chain reaches the `drawing` package,
    // which the pure exports above must not drag into unit tests.
    const { transformTilePoint } = await import(
      "@local/extensions/geometry/transform-sheet-points.js"
    );

    const limits = sheet.limits;
    const r = sheet.rotation || 0;

    const clip = searchClip(pts);
    const cp = [
      { x: clip.x, y: clip.y + clip.height },
      { x: clip.x + clip.width, y: clip.y + clip.height },
      { x: clip.x + clip.width, y: clip.y },
      { x: clip.x, y: clip.y },
      { x: clip.x, y: clip.y + clip.height },
    ].map((p) => sheetPoint(transformTilePoint, p, limits, r));

    const overlaps = await rpc("find_overlapping_text_objects", {
      document_id: doc.id,
      pages: [page.id],
      regions: [cp],
    });

    if (overlaps.error) throw new Error("RPC error: " + overlaps.error);

    const ctr = midpoint(cp[0], cp[2]);
    return nearestDimension(
      overlaps.data[0].overlapping_regions[0],
      ctr,
      parse,
    );
  } catch (error) {
    // A missing suggestion is not an error the user needs to see.
    console.log(error);
    return null;
  }
}

export { removeNonDimensionText, nearestDimension, readDimensionSuggestion };
